// Customer (LED pole) display feed — web replacement for the Android serial
// LED display (activity_led_control2.xml). The Bill screen publishes a
// snapshot of the in-progress bill; the /led-display page subscribes and
// renders it customer-facing. Live snapshots travel cross-tab over
// BroadcastChannel, so the display can run in a second tab/window dragged onto a
// customer-facing monitor. The latest snapshot is also persisted to IndexedDB so
// a freshly-opened display tab can show the current bill before the next update.

import { cachedKey } from './idb';

export interface LedLine {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface LedSnapshot {
  state: 'idle' | 'billing' | 'paid';
  storeName: string;
  currency: string;
  lines: LedLine[];
  totalQty: number;
  billAmt: number;
  discount: number;
  tax: number;
  finalTotal: number;
  // Set in the 'paid' state — the settled bill's number and amount.
  paidBillNo?: string;
  paidTotal?: number;
  updatedAt: string;
}

const CHANNEL = 'sconnect-led';

// Last snapshot, cached in-memory (readable synchronously) and persisted to
// IndexedDB so a newly-opened display tab has something to show immediately.
const ledCache = cachedKey<LedSnapshot | null>('sconnect.ledDisplay', null);

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null;

export function publishLedSnapshot(snap: Omit<LedSnapshot, 'updatedAt'>): void {
  const full: LedSnapshot = { ...snap, updatedAt: new Date().toISOString() };
  // Persist for the next fresh display load; the live broadcast below updates
  // any display tab that is already open.
  ledCache.set(full);
  channel?.postMessage(full);
}

export function readLedSnapshot(): LedSnapshot | null {
  return ledCache.get();
}

// Subscribe to snapshot updates broadcast from the billing tab. Returns an
// unsubscribe function.
export function subscribeLed(cb: (snap: LedSnapshot) => void): () => void {
  const onMessage = (e: MessageEvent) => cb(e.data as LedSnapshot);
  channel?.addEventListener('message', onMessage);
  return () => {
    channel?.removeEventListener('message', onMessage);
  };
}
